import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CreatePlanModal from "../../components/CreatePlanModal";
import PlanCatalogTable from "../../components/PlanCatalogTable";
import { isPlanAssignable } from "../../lib/planStatus";
import {
  assignPlanToTenant,
  createPlanCatalog,
  fetchPlanCatalog,
  fetchPlatformTenants,
  updatePlanCatalogStatus,
} from "../../lib/opsGraphql";

export default function PlanManagement() {
  const [searchParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tenantId, setTenantId] = useState(searchParams.get("tenant") || "");
  const [planId, setPlanId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [tenantRows, catalog] = await Promise.all([
        fetchPlatformTenants(),
        fetchPlanCatalog(),
      ]);
      setTenants(tenantRows.filter((t) => t.status === "active" || t.status === "suspended"));
      setPlans(catalog);
    } catch (e) {
      setError(e.message || "Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const fromUrl = searchParams.get("tenant");
    if (fromUrl) setTenantId(fromUrl);
  }, [searchParams]);

  const assignablePlans = useMemo(() => plans.filter(isPlanAssignable), [plans]);

  useEffect(() => {
    if (!planId && assignablePlans.length) {
      setPlanId(assignablePlans[0].plan_id);
    }
  }, [assignablePlans, planId]);

  const handleAssign = async () => {
    if (!tenantId || !planId) {
      setError("Seleccioná tenant y plan");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await assignPlanToTenant(tenantId, planId);
      const tenant = tenants.find((t) => t.tenant_id === tenantId);
      const plan = plans.find((p) => p.plan_id === planId);
      setSuccess(
        `Plan «${plan?.name || planId}» asignado a ${tenant?.name || tenantId} correctamente.`
      );
    } catch (e) {
      setError(e.message || "No se pudo asignar el plan");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async (input) => {
    setLoading(true);
    await createPlanCatalog(input);
    setSuccess(`Plan «${input.name}» creado.`);
    await loadData();
    setLoading(false);
  };

  const handleToggleStatus = async (id, status) => {
    setLoading(true);
    setError("");
    try {
      await updatePlanCatalogStatus(id, status);
      setSuccess(`Plan ${status === "active" ? "activado" : "desactivado"}.`);
      await loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="ops-card">
        <h2>Gestión de planes</h2>
        <p className="ops-subtitle">Asigná un plan vigente a un tenant en un paso.</p>

        {error && <p className="ops-error">{error}</p>}
        {success && <p className="ops-info">{success}</p>}

        <div className="ops-assign-row">
          <label>
            Tenant
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              <option value="">Seleccionar tenant…</option>
              {tenants.map((t) => (
                <option key={t.tenant_id} value={t.tenant_id}>
                  {t.name} ({t.tenant_id})
                </option>
              ))}
            </select>
          </label>
          <label>
            Plan
            <select value={planId} onChange={(e) => setPlanId(e.target.value)}>
              <option value="">Seleccionar plan…</option>
              {assignablePlans.map((p) => (
                <option key={p.plan_id} value={p.plan_id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="ops-btn"
            disabled={loading || !tenantId || !planId}
            onClick={handleAssign}
          >
            Asignar
          </button>
        </div>
        {assignablePlans.length === 0 && (
          <p className="ops-subtitle">No hay planes activos y vigentes para asignar.</p>
        )}
      </div>

      <div className="ops-card">
        <div className="ops-card-header-row">
          <h2>Catálogo de planes</h2>
          <button type="button" className="ops-btn" onClick={() => setShowCreateModal(true)}>
            Nuevo plan
          </button>
        </div>
        <PlanCatalogTable plans={plans} loading={loading} onToggleStatus={handleToggleStatus} />
      </div>

      {showCreateModal && (
        <CreatePlanModal
          loading={loading}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreatePlan}
        />
      )}
    </>
  );
}
